import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../services/api';
import './AgencyManagementPage.css';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Typography, Button, IconButton, Switch, TextField, FormControlLabel, Box, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faPencil, faTrash, faEye} from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';
import AgencyFormModal from './AgencyFormModal';
import { isCurrentUserSiteAdmin } from "./UserManagementPage";
import { useOri } from '../../context/OriContext';
import LoadingIcon from '../LoadingIcon';

export interface Agency {
    name: string;
    active: boolean;
    authorized_oris: string[];
    type: "AGENCY" | "ORGANIZATION";
    ori: string;
}

const saveAgencyApi = async (agency: Agency, isNew: boolean) => {
    const method = isNew ? 'POST' : 'PUT';
    const path = isNew ? '/api/risc/agencies' : `/api/risc/agencies/${agency.ori}`;
    const response = await apiFetch(path, {
        method,
        body: JSON.stringify(isNew ? {agencies: [agency]} : agency),
    });
    if (!response.ok) throw new Error('Failed to save agency');
    return response.json();
};

const deleteAgencyApi = async (ori: string) => {
    const response = await apiFetch(`/api/risc/agencies/${ori}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete agency');
};

const AuthorizedOrisDialog: React.FC<{ open: boolean, onClose: () => void, oris: string[], allAgencies: Agency[] }> = ({ open, onClose, oris, allAgencies }) => {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Authorized ORIs</DialogTitle>
            <DialogContent>
                <List>
                    {(oris ?? []).map(ori => {
                        const agency = allAgencies.find(a => a.ori === ori);
                        return (
                            <ListItem key={ori}>
                                <ListItemText primary={agency?.name || 'Unknown Agency'} secondary={ori} />
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>
        </Dialog>
    );
};

const AgencyManagementPage: React.FC = () => {
    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [filteredAgencies, setFilteredAgencies] = useState<Agency[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOrisDialogOpen, setIsOrisDialogOpen] = useState(false);
    const [currentAgency, setCurrentAgency] = useState<Agency | null>(null);
    const [selectedOris, setSelectedOris] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const isSiteAdmin = isCurrentUserSiteAdmin();
    const { setSelectedOri, fetchAllAgencies, refreshAvailableOris } = useOri();

    const fetchAgencies = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const agenciesData = await fetchAllAgencies();
            setAgencies(agenciesData || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    }, [fetchAllAgencies]);

    useEffect(() => { // Note: fetchAllAgencies is stable from useCallback in context
        fetchAgencies();
    }, [fetchAgencies]);

    useEffect(() => {
        const lowercasedFilter = searchTerm.toLowerCase();
        const filteredData = agencies.filter(item =>
            ((item.name || '').toLowerCase().includes(lowercasedFilter) ||
            (item.ori || '').toLowerCase().includes(lowercasedFilter)) &&
            (!showActiveOnly || item.active)
        );
        setFilteredAgencies(filteredData);
    }, [searchTerm, agencies, showActiveOnly]);

    const handleAddAgency = () => {
        setCurrentAgency(null);
        setIsModalOpen(true);
    };

    const handleEditAgency = (agency: Agency) => {
        setCurrentAgency(agency);
        setIsModalOpen(true);
    };

    const handleShowOris = (oris: string[]) => {
        setSelectedOris(oris);
        setIsOrisDialogOpen(true);
    };

    const handleDeleteAgency = async (ori: string) => {
        if (window.confirm('Are you sure you want to delete this agency?')) {
            try {
                await deleteAgencyApi(ori);
                await fetchAgencies(); // Refetch all agencies for the page
                refreshAvailableOris(); // Refresh the list in the navbar
            } catch (err) {
                setError('Failed to delete agency.');
            }
        }
    };

    const handleSaveAgency = async (agency: Agency) => {
        try {
            await saveAgencyApi(agency, !currentAgency);
            setIsModalOpen(false);
            await fetchAgencies(); // Refetch all agencies for the page
            refreshAvailableOris(); // Refresh the list in the navbar
        } catch (err)
            {
            setError('Failed to save agency.');
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const canEdit = (agency: Agency) => {
        if (isSiteAdmin) return true;
        const userOri = sessionStorage.getItem('agencyOri');
        const userAuthorizedOris = JSON.parse(sessionStorage.getItem('authorizedOris') || '[]');
        return agency.ori === userOri || userAuthorizedOris.includes(agency.ori);
    }

    return (
            <>
            <LoadingIcon isLoading={isLoading} showOverlay={true} />
            <Paper sx={{ p: 2, m: 1 }}>
                <div className="agency-management-header">
                    <Typography variant="h6" gutterBottom component="div">
                        Agency Management
                    </Typography>
                    {isSiteAdmin && <Button variant="contained" onClick={handleAddAgency}>Add Agency</Button>}
                </div>
                <Box my={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <TextField
                        label="Search by Name or ORI"
                        variant="outlined"
                        size="small"
                        sx={{ flexGrow: 1, minWidth: 240 }}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <FormControlLabel
                        control={<Switch checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} />}
                        label="Active only"
                    />
                </Box>
                {error && <p className="error-message">{error}</p>}
                {!isLoading && !error && (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>ORI</TableCell>
                                        <TableCell>Active</TableCell>
                                        <TableCell>Authorized ORIs</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAgencies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(agency => (
                                        <TableRow key={agency.ori}>
                                            <TableCell>{agency.name}</TableCell>
                                            <TableCell className="mono-id">{agency.ori}</TableCell>
                                            <TableCell>
                                                <Switch checked={!!agency.active} disabled />
                                            </TableCell>
                                            <TableCell>
                                                <Button onClick={() => handleShowOris(agency.authorized_oris ?? [])}>{(agency.authorized_oris ?? []).length}</Button>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => setSelectedOri(agency.ori)} title="View Agency">
                                                    <FontAwesomeIcon icon={faEye} swapOpacity />
                                                </IconButton>
                                                {canEdit(agency) &&
                                                    <IconButton onClick={() => handleEditAgency(agency)} title="Edit Agency">
                                                        <FontAwesomeIcon icon={faPencil} swapOpacity />
                                                    </IconButton>
                                                }
                                                {isSiteAdmin &&
                                                    <IconButton onClick={() => handleDeleteAgency(agency.ori)} title="Delete Agency" color="warning">
                                                        <FontAwesomeIcon icon={faTrash} swapOpacity />
                                                    </IconButton>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={filteredAgencies.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}
            {isModalOpen && (
                <AgencyFormModal
                    agency={currentAgency}
                    agencies={agencies}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveAgency}
                />
            )}
            <AuthorizedOrisDialog
                open={isOrisDialogOpen}
                onClose={() => setIsOrisDialogOpen(false)}
                oris={selectedOris}
                allAgencies={agencies}
            />
        </Paper>
    </>
    );
};

export default AgencyManagementPage;
